import {
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    TableCell,
    TableRow,
    TablePagination,
    useTheme,
} from '@material-ui/core'
import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { CellData, HeadCell, RowData, SimpleTable } from '../../components'
import {
    formatScheduleDateInZone,
    getUiCamelCase,
    resolveRoute,
    Routes,
} from '../../helpers'
import { useTimezone } from '../../contexts/timezone'
import {
    ARCHIVE_INSPECTIONS,
    getAssignedUsersDisplay,
    Inspection,
    InspectionType,
    User,
} from '../../models'
import { useStyles } from '../../styles'
import EditIcon from '@material-ui/icons/Edit'
import DeleteForeverIcon from '@material-ui/icons/DeleteForever'
import {
    patchInspection,
    deleteInspection,
    UpdateInspectionAxiosReq,
} from '../../store'
import { useAppDispatch } from '../../hooks'
import { toast } from 'react-toastify'
import { hasPermission } from '../../models/Users/services'
import { typographyStyle } from '../../theme/typographyStyle'

export interface InspectionRow extends RowData {
    name: CellData<string>
    inspection_type: CellData<InspectionType>
    schedule: CellData<string>
    start_date: CellData<string>
    end_date: CellData<string>
    progress: CellData<string>
    assigned_to: CellData<string>
    inspection: CellData<Inspection>
}

interface Props {
    inspectionList?: Inspection[]
    selectedInspection?: Inspection
    setSelectedInspection: (ins?: Inspection) => void
    setDrawerOpen: (open: boolean) => void
    active: boolean
    page?: number
    rowsPerPage?: number
    totalCount?: number
    onPageChange?: (event: unknown, newPage: number) => void
    onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onSort?: (property: string, direction: 'asc' | 'desc') => void
    sortBy?: string
    sortDirection?: 'asc' | 'desc'
    isLoading?: boolean
    onInspectionArchived?: (archivedInspectionId: number) => void
    onInspectionDeleted?: (deletedInspectionId: number) => void
    workspaceUser?: User
}

export const InspectionListTable = (props: Props) => {
    const {
        inspectionList,
        selectedInspection,
        setSelectedInspection,
        setDrawerOpen,
        active,
        page = 0,
        rowsPerPage = 10,
        totalCount = 0,
        onPageChange,
        onRowsPerPageChange,
        onSort,
        sortBy = 'start_date',
        sortDirection = 'desc',
        isLoading = false,
        onInspectionArchived,
        onInspectionDeleted,
        workspaceUser,
    } = props

    const theme = useTheme()
    const classes = useStyles()
    const { currentTimezone, propertyTimezone } = useTimezone()
    const displayZone = propertyTimezone ?? currentTimezone
    const history = useHistory()
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
    const [
        inspectionToDelete,
        setInspectionToDelete,
    ] = useState<Inspection | null>(null)

    const dispatch = useAppDispatch()

    const headCellStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.h6),
        fontWeight: 500,
    }

    const headCells: HeadCell<InspectionRow>[] = [
        {
            disablePadding: false,
            align: 'left',
            id: 'name',
            label: 'Name',
            style: headCellStyle,
            backendSort: ['name'],
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'inspection_type_category',
            label: 'Type Category',
            style: headCellStyle,
            backendSort: ['inspection_type_category'],
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'inspection_type',
            label: 'Inspection Type',
            style: headCellStyle,
            backendSort: ['inspection_type_name'],
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'schedule',
            label: 'Schedule',
            style: headCellStyle,
            backendSort: ['schedule_name'],
            sortable: false,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'start_date',
            label: 'Start Date',
            style: headCellStyle,
            backendSort: ['start_date'],
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'end_date',
            label: 'End Date',
            style: headCellStyle,
            backendSort: ['end_date'],
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'progress',
            label: 'Progress',
            style: headCellStyle,
            sortable: false,
        },
        {
            disablePadding: false,
            align: 'left',
            id: 'assigned_to',
            label: 'Assigned To',
            style: headCellStyle,
            sortable: false,
        },
        {
            disablePadding: false,
            align: 'left',
            id: '',
            label: '',
            style: headCellStyle,
            sortable: false,
        },
    ]

    const activeInspections = inspectionList !== undefined && active

    if (activeInspections) {
        headCells.push({
            disablePadding: false,
            align: 'left',
            id: '',
            label: '',
            style: headCellStyle,
            sortable: false,
        })
        headCells.push({
            disablePadding: false,
            align: 'left',
            id: '',
            label: '',
            style: headCellStyle,
            sortable: false,
        })
    }

    const createInspectionRow = (inspection: Inspection): InspectionRow => {
        const uiTotal = inspection.ui_total ?? 0
        const uiComplete = inspection.ui_complete ?? 0

        const safeDenominator = uiTotal > 0 ? uiTotal : 1

        const progressPercent = (uiComplete / safeDenominator) * 100
        const progressStr = `${uiComplete} / ${uiTotal} (${progressPercent.toFixed(
            1,
        )}%)`

        return {
            name: { value: inspection.name, sortValue: inspection.name },
            inspection_type_category: {
                value: inspection.inspection_type.category,
                sortValue: inspection?.inspection_type?.category,
            },
            inspection_type: {
                value: inspection.inspection_type,
                sortValue: inspection?.inspection_type?.name,
            },
            schedule: {
                value: inspection.schedule?.name ?? 'N/A',
                sortValue: inspection.schedule?.name ?? 'N/A',
            },
            start_date: {
                value: inspection.start_date,
                sortValue: inspection.start_date,
            },
            end_date: {
                value: inspection.end_date,
                sortValue: inspection.end_date,
            },
            progress: {
                value: progressStr,
                sortValue: progressStr,
            },
            assigned_to: {
                value: getAssignedUsersDisplay(
                    inspection.assigned_users,
                    inspection.assigned_to,
                ),
                sortValue: getAssignedUsersDisplay(
                    inspection.assigned_users,
                    inspection.assigned_to,
                ),
            },
            inspection: {
                value: inspection,
                sortValue: inspection.id,
            },
        }
    }

    const canArchiveInspections = hasPermission(
        workspaceUser,
        ARCHIVE_INSPECTIONS,
    )

    return (
        <>
            <div style={{ position: 'relative' }}>
                <SimpleTable<InspectionRow>
                    rows={
                        inspectionList && inspectionList?.length > 0
                            ? inspectionList?.map((ins) =>
                                  createInspectionRow(ins),
                              ) ?? []
                            : []
                    }
                    backendPagination={{
                        onSortChange: (sort: string[], dir: 'asc' | 'desc') => {
                            if (onSort && sort.length > 0) {
                                onSort(sort[0], dir)
                            }
                        },
                    }}
                    hidePagination={!!onPageChange}
                    maxHeight="calc(100vh - 300px)"
                    render={(row) => {
                        const startDateStr = formatScheduleDateInZone(
                            row.start_date.value,
                            displayZone,
                        )

                        const endDateStr = formatScheduleDateInZone(
                            row.end_date.value,
                            displayZone,
                        )

                        const rowStyle: React.CSSProperties = {
                            cursor: 'pointer',
                        }

                        const selected =
                            row.inspection.value.id === selectedInspection?.id

                        if (selected) {
                            rowStyle.backgroundColor =
                                theme.palette.primary.light
                        }

                        return (
                            <TableRow
                                key={`INSPECTION_ROW_${row.inspection.value.id}`}
                                className={classes.hoverGrey200}
                                style={rowStyle}
                                onClick={() => {
                                    history.push(
                                        resolveRoute(
                                            Routes.inspectionDetail,
                                            ':id',
                                            row.inspection.value.id,
                                        ),
                                    )
                                }}
                            >
                                <TableCell>
                                    <span>{row.name.value}</span>
                                    {row.inspection.value.share_with_tenant && (
                                        <Chip
                                            label="Shared with tenant"
                                            size="small"
                                            variant="outlined"
                                            style={{
                                                marginLeft: 8,
                                                height: 22,
                                                fontSize: '0.7rem',
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell>
                                    {getUiCamelCase(
                                        row.inspection_type.value.category,
                                    )}
                                </TableCell>
                                <TableCell>
                                    {row.inspection_type?.value?.name}
                                </TableCell>
                                <TableCell>{row.schedule.value}</TableCell>
                                <TableCell>{startDateStr}</TableCell>
                                <TableCell>{endDateStr}</TableCell>
                                <TableCell>{row.progress.value}</TableCell>
                                <TableCell>{row.assigned_to.value}</TableCell>
                                <TableCell>
                                    <IconButton
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            setSelectedInspection(
                                                row.inspection.value,
                                            )
                                            setDrawerOpen(true)
                                        }}
                                    >
                                        <EditIcon
                                            className={`${classes.fontSecondaryMain} ${classes.fontHoverSecondaryDark}`}
                                        />
                                    </IconButton>
                                </TableCell>
                                {activeInspections && (
                                    <TableCell
                                        style={{ boxShadow: 'none', zIndex: 1 }}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Button
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (
                                                    confirm(
                                                        `Do you want to archive ${row.name.value}`,
                                                    )
                                                ) {
                                                    const req: UpdateInspectionAxiosReq = {
                                                        id:
                                                            row.inspection.value
                                                                .id,
                                                        body: {
                                                            active: false,
                                                        },
                                                    }

                                                    dispatch(
                                                        patchInspection(req),
                                                    ).then(() => {
                                                        toast.success(
                                                            'Inspection archived',
                                                        )
                                                        // Notify parent component to update the list
                                                        if (
                                                            onInspectionArchived
                                                        ) {
                                                            onInspectionArchived(
                                                                row.inspection
                                                                    .value.id,
                                                            )
                                                        }
                                                    })
                                                }
                                            }}
                                            variant="contained"
                                            style={{
                                                backgroundColor: canArchiveInspections
                                                    ? '#008C85'
                                                    : '#B0B0B0',
                                                color: 'white',
                                                textTransform: 'none',
                                                cursor: canArchiveInspections
                                                    ? 'pointer'
                                                    : 'not-allowed',
                                                opacity: canArchiveInspections
                                                    ? 1
                                                    : 0.6,
                                            }}
                                            disabled={!canArchiveInspections}
                                        >
                                            Archive
                                        </Button>
                                    </TableCell>
                                )}
                                {activeInspections &&
                                    row.inspection.value.ui_complete === 0 && (
                                        <TableCell
                                            style={{
                                                boxShadow: 'none',
                                                zIndex: 1,
                                                width: '40px',
                                                padding: '0 8px',
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    setInspectionToDelete(
                                                        row.inspection.value,
                                                    )
                                                    setDeleteDialogOpen(true)
                                                }}
                                                disabled={
                                                    !canArchiveInspections
                                                }
                                            >
                                                <DeleteForeverIcon
                                                    style={{
                                                        color: canArchiveInspections
                                                            ? theme.palette
                                                                  .error.main
                                                            : theme.palette
                                                                  .action
                                                                  .disabled,
                                                    }}
                                                />
                                            </IconButton>
                                        </TableCell>
                                    )}
                            </TableRow>
                        )
                    }}
                    orderByDefault="start_date"
                    headCells={headCells}
                ></SimpleTable>

                {/* Custom pagination for backend pagination */}
                {onPageChange && onRowsPerPageChange && (
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onChangePage={onPageChange}
                        onChangeRowsPerPage={onRowsPerPageChange}
                    />
                )}
                {/* Loading overlay */}
                {isLoading && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 1000,
                            pointerEvents: 'none',
                        }}
                    >
                        <CircularProgress />
                    </div>
                )}
            </div>

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Inspection
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this inspection? This
                        will permanently delete all inspection data related to
                        this inspection. This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setDeleteDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => {
                            if (inspectionToDelete) {
                                dispatch(deleteInspection(inspectionToDelete))
                                    .then(() => {
                                        toast.success('Inspection deleted')
                                        setDeleteDialogOpen(false)
                                        // Notify parent component to update the list
                                        if (onInspectionDeleted) {
                                            onInspectionDeleted(
                                                inspectionToDelete.id,
                                            )
                                        }
                                    })
                                    .catch(() => {})
                            }
                        }}
                        color="secondary"
                        variant="contained"
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
